/**
 * MyTimeOffRequests.jsx
 * Employee view of their time-off request history and status
 * Now with document upload integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from './axiosConfig';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Add,
  CalendarToday,
  CheckCircle,
  Cancel,
  AccessTime,
  Refresh,
  ExpandMore,
  ExpandLess,
  Warning,
  CloudUpload,
  AttachFile,
  Description,
  Image as ImageIcon,
  PictureAsPdf,
  Download,
  VerifiedUser
} from '@mui/icons-material';
import { DocumentUpload } from './DocumentUpload';

// Leave type configuration
const LEAVE_CONFIG = {
  standard_holiday: { name: 'Annual Leave', icon: '🌴', color: '#0D9488', needsDocs: false },
  sickness: { name: 'Sickness', icon: '🤒', color: '#DC2626', needsDocs: true, docType: 'fit_note', docPrompt: 'Upload fit note if absence is longer than 7 days' },
  maternity: { name: 'Maternity Leave', icon: '🤰', color: '#7C3AED', needsDocs: true, docType: 'matb1', docPrompt: 'Upload MATB1 certificate from your midwife' },
  paternity: { name: 'Paternity Leave', icon: '👨‍👧', color: '#7C3AED', needsDocs: false },
  adoption: { name: 'Adoption Leave', icon: '👨‍👩‍👧', color: '#7C3AED', needsDocs: true, docType: 'other', docPrompt: 'Upload matching certificate' },
  shared_parental: { name: 'Shared Parental Leave', icon: '👪', color: '#7C3AED', needsDocs: false },
  neonatal_care: { name: 'Neonatal Care Leave', icon: '👶', color: '#7C3AED', needsDocs: true, docType: 'medical_evidence', docPrompt: 'Upload hospital documentation' },
  unpaid_parental: { name: 'Unpaid Parental Leave', icon: '👨‍👩‍👧‍👦', color: '#7C3AED', needsDocs: false },
  carers_leave: { name: "Carer's Leave", icon: '🤝', color: '#0284C7', needsDocs: false },
  emergency_dependant: { name: 'Emergency Dependant Leave', icon: '🚨', color: '#0284C7', needsDocs: false },
  parental_bereavement: { name: 'Parental Bereavement', icon: '🕯️', color: '#0284C7', needsDocs: true, docType: 'other', docPrompt: 'Upload supporting documentation if available' },
  jury_service: { name: 'Jury Service', icon: '⚖️', color: '#64748B', needsDocs: true, docType: 'other', docPrompt: 'Upload jury summons' }
};

// Status configuration
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'warning', icon: <AccessTime /> },
  acknowledged: { label: 'Acknowledged', color: 'info', icon: <AccessTime /> },
  approved: { label: 'Approved', color: 'success', icon: <CheckCircle /> },
  refused: { label: 'Refused', color: 'error', icon: <Cancel /> },
  cancelled: { label: 'Cancelled', color: 'default', icon: <Cancel /> },
  withdrawn: { label: 'Withdrawn', color: 'default', icon: <Cancel /> }
};

// Refusal reason display names
const getRefusalReasonName = (code) => {
  const reasons = {
    'staffing_levels': 'Insufficient staffing cover',
    'peak_period': 'Peak trading period',
    'notice_period': 'Insufficient notice given',
    'consecutive_requests': 'Clashes with other approved leave',
    'training_event': 'Essential training or meeting',
    'probation': 'During probationary period'
  };
  return reasons[code] || code;
};

const MyTimeOffRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [expandedCard, setExpandedCard] = useState(null);
  const [cancelDialog, setCancelDialog] = useState({ open: false, request: null });
  const [uploadDialog, setUploadDialog] = useState({ open: false, request: null });
  const [documents, setDocuments] = useState({});

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/time-off/requests');
      setRequests(response.data.requests || []);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError('Failed to load your requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch documents for a specific request
  const fetchDocuments = useCallback(async (requestId) => {
    try {
      const response = await api.get(`/time-off-docs/record/time_off_requests/${requestId}`);
      setDocuments(prev => ({
        ...prev,
        [requestId]: response.data.documents || []
      }));
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Fetch documents when expanding a card
  useEffect(() => {
    if (expandedCard && !documents[expandedCard]) {
      fetchDocuments(expandedCard);
    }
  }, [expandedCard, documents, fetchDocuments]);

  // Filter requests based on tab
  const getFilteredRequests = () => {
    switch (tabValue) {
      case 1:
        return requests.filter(r => ['pending', 'acknowledged'].includes(r.status));
      case 2:
        return requests.filter(r => r.status === 'approved');
      case 3:
        return requests.filter(r => ['refused', 'cancelled', 'withdrawn'].includes(r.status));
      default:
        return requests;
    }
  };

  const filteredRequests = getFilteredRequests();

  // Get leave config with fallback
  const getLeaveConfig = (reasonType) => {
    return LEAVE_CONFIG[reasonType] || {
      name: reasonType?.replace(/_/g, ' ') || 'Unknown',
      icon: '📋',
      color: '#6B7280',
      needsDocs: false
    };
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Handle cancel request
  const handleCancelRequest = async () => {
    if (!cancelDialog.request) return;
    
    try {
      await api.post(`/time-off/requests/${cancelDialog.request.id}/cancel`);
      setCancelDialog({ open: false, request: null });
      fetchRequests();
    } catch (err) {
      console.error('Error cancelling request:', err);
      setError(err.response?.data?.error || 'Failed to cancel request');
    }
  };

  // Handle document upload complete
  const handleUploadComplete = (requestId) => {
    fetchDocuments(requestId);
    setUploadDialog({ open: false, request: null });
  };

  // Download document
  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/time-off-docs/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Failed to download document');
    }
  };

  // Count by status for tabs
  const pendingCount = requests.filter(r => ['pending', 'acknowledged'].includes(r.status)).length;
  const approvedCount = requests.filter(r => r.status === 'approved').length;
  const refusedCount = requests.filter(r => ['refused', 'cancelled', 'withdrawn'].includes(r.status)).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            My Time Off
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage your leave requests
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchRequests}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/time-off/request')}
          >
            New Request
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="fullWidth"
        >
          <Tab label={`All (${requests.length})`} />
          <Tab 
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Pending
                {pendingCount > 0 && (
                  <Chip label={pendingCount} size="small" color="warning" />
                )}
              </Box>
            } 
          />
          <Tab label={`Approved (${approvedCount})`} />
          <Tab label={`Refused/Cancelled (${refusedCount})`} />
        </Tabs>
      </Paper>

      {/* Empty state */}
      {filteredRequests.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CalendarToday sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {tabValue === 0 ? 'No requests yet' : 'No requests in this category'}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {tabValue === 0 
              ? "You haven't submitted any time-off requests yet."
              : "No requests match this filter."}
          </Typography>
          {tabValue === 0 && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate('/time-off/request')}
            >
              Request Time Off
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredRequests.map((request) => {
            const config = getLeaveConfig(request.reason_type);
            const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
            const isExpanded = expandedCard === request.id;
            const canCancel = ['pending', 'acknowledged'].includes(request.status);
            const canUpload = ['pending', 'acknowledged', 'approved'].includes(request.status);
            const requestDocs = documents[request.id] || [];
            const hasDocuments = requestDocs.length > 0;
            
            return (
              <Grid item xs={12} key={request.id}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    borderLeft: `4px solid ${config.color}`,
                    '&:hover': { boxShadow: 2 }
                  }}
                >
                  <CardContent sx={{ pb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      {/* Left: Type & Status */}
                      <Grid item xs={12} sm={4}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="h6" component="span">
                            {config.icon}
                          </Typography>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {config.name}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Chip
                                icon={statusConfig.icon}
                                label={statusConfig.label}
                                color={statusConfig.color}
                                size="small"
                                variant="outlined"
                              />
                              {hasDocuments && (
                                <Tooltip title={`${requestDocs.length} document(s) attached`}>
                                  <AttachFile sx={{ fontSize: 16, color: 'text.secondary' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Grid>

                      {/* Middle: Dates */}
                      <Grid item xs={12} sm={5}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday sx={{ color: 'text.secondary', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {formatDate(request.start_date)}
                              {request.end_date && request.end_date !== request.start_date && (
                                <> → {formatDate(request.end_date)}</>
                              )}
                            </Typography>
                            {request.total_days && (
                              <Typography variant="caption" color="text.secondary">
                                {request.total_days} working day{request.total_days !== 1 ? 's' : ''}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Right: Actions */}
                      <Grid item xs={12} sm={3}>
                        <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                          {request.is_short_notice && request.status === 'pending' && (
                            <Tooltip title="Short notice request">
                              <Warning color="warning" fontSize="small" />
                            </Tooltip>
                          )}
                          {config.needsDocs && !hasDocuments && canUpload && (
                            <Tooltip title={config.docPrompt || 'Upload document'}>
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setUploadDialog({ open: true, request });
                                }}
                              >
                                <CloudUpload fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <IconButton 
                            size="small" 
                            onClick={() => setExpandedCard(isExpanded ? null : request.id)}
                          >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Document prompt for leave types that need docs */}
                    {config.needsDocs && !hasDocuments && canUpload && (
                      <Alert 
                        severity="info" 
                        sx={{ mt: 2, py: 0.5 }}
                        action={
                          <Button 
                            color="inherit" 
                            size="small"
                            startIcon={<CloudUpload />}
                            onClick={() => setUploadDialog({ open: true, request })}
                          >
                            Upload
                          </Button>
                        }
                      >
                        {config.docPrompt || 'Supporting documents may be required'}
                      </Alert>
                    )}

                    {/* Refused reason banner */}
                    {request.status === 'refused' && request.refusal_reason && (
                      <Alert severity="error" sx={{ mt: 2, py: 0.5 }}>
                        <Typography variant="body2">
                          <strong>Refused:</strong> {getRefusalReasonName(request.refusal_reason)}
                        </Typography>
                      </Alert>
                    )}

                    {/* Expanded details */}
                    {isExpanded && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Submitted
                            </Typography>
                            <Typography variant="body2">
                              {formatDate(request.created_at)}
                            </Typography>
                          </Grid>
                          
                          {request.decision_date && (
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Decision Date
                              </Typography>
                              <Typography variant="body2">
                                {formatDate(request.decision_date)}
                              </Typography>
                            </Grid>
                          )}
                          
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Request ID
                            </Typography>
                            <Typography variant="body2">
                              #{request.id}
                            </Typography>
                          </Grid>

                          {request.is_short_notice && (
                            <Grid item xs={6} sm={3}>
                              <Chip 
                                label="Short Notice" 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                              />
                            </Grid>
                          )}
                        </Grid>

                        {/* Documents section */}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Documents
                          </Typography>
                          
                          {requestDocs.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No documents attached
                            </Typography>
                          ) : (
                            <List dense disablePadding>
                              {requestDocs.map((doc) => (
                                <ListItem 
                                  key={doc.id}
                                  sx={{ 
                                    bgcolor: 'grey.50', 
                                    borderRadius: 1, 
                                    mb: 0.5,
                                    pr: 10
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    {doc.is_image ? (
                                      <ImageIcon color="primary" fontSize="small" />
                                    ) : doc.is_pdf ? (
                                      <PictureAsPdf color="error" fontSize="small" />
                                    ) : (
                                      <Description fontSize="small" />
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={doc.document_type_name || doc.document_type}
                                    secondary={doc.original_filename}
                                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                    secondaryTypographyProps={{ variant: 'caption' }}
                                  />
                                  <ListItemSecondaryAction>
                                    {doc.is_verified && (
                                      <Tooltip title="Verified by manager">
                                        <VerifiedUser 
                                          color="success" 
                                          sx={{ fontSize: 18, mr: 1 }} 
                                        />
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Download">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handleDownload(doc)}
                                      >
                                        <Download fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              ))}
                            </List>
                          )}

                          {/* Upload button */}
                          {canUpload && (
                            <Button
                              size="small"
                              startIcon={<CloudUpload />}
                              onClick={() => setUploadDialog({ open: true, request })}
                              sx={{ mt: 1 }}
                            >
                              Add Document
                            </Button>
                          )}
                        </Box>

                        {/* Cancel button for pending requests */}
                        {canCancel && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: '1px dashed', borderColor: 'divider' }}>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => setCancelDialog({ open: true, request })}
                            >
                              Cancel Request
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Cancel confirmation dialog */}
      <Dialog
        open={cancelDialog.open}
        onClose={() => setCancelDialog({ open: false, request: null })}
        maxWidth="sm"
      >
        <DialogTitle>Cancel Request?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this time-off request?
          </Typography>
          {cancelDialog.request && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{getLeaveConfig(cancelDialog.request.reason_type).name}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(cancelDialog.request.start_date)} → {formatDate(cancelDialog.request.end_date)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialog({ open: false, request: null })}>
            Keep Request
          </Button>
          <Button color="error" variant="contained" onClick={handleCancelRequest}>
            Yes, Cancel It
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document upload dialog */}
      <Dialog
        open={uploadDialog.open}
        onClose={() => setUploadDialog({ open: false, request: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Upload Document
        </DialogTitle>
        <DialogContent>
          {uploadDialog.request && (
            <>
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{getLeaveConfig(uploadDialog.request.reason_type).name}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(uploadDialog.request.start_date)} → {formatDate(uploadDialog.request.end_date)}
                </Typography>
              </Box>
              
              {getLeaveConfig(uploadDialog.request.reason_type).docPrompt && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {getLeaveConfig(uploadDialog.request.reason_type).docPrompt}
                </Alert>
              )}
              
              <DocumentUpload
                sourceTable="time_off_requests"
                sourceId={uploadDialog.request.id}
                documentType={getLeaveConfig(uploadDialog.request.reason_type).docType || 'other'}
                onUploadComplete={() => handleUploadComplete(uploadDialog.request.id)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog({ open: false, request: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyTimeOffRequests;